import React from "react";
import PropTypes from "prop-types";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./../../assets/scss/common.scss";

// Reusable section header component
const SectionHeader = ({ width = "150px" }) => (
  <div className="mb-3 sectionHeaderSkeleton">
    <Skeleton width={width} height={20} />
  </div>
);
SectionHeader.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

// Reusable form field component
const FormField = ({ labelWidth = "70%", fieldHeight = 30 }) => (
  <div>
    <Skeleton width={labelWidth} height={15} className="mb-2" />
    <Skeleton width="100%" height={fieldHeight} />
  </div>
);
FormField.propTypes = {
  labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fieldHeight: PropTypes.number
};

// Reusable form field with icon
const FormFieldWithIcon = ({ labelWidth = "70%", fieldHeight = 30 }) => (
  <div>
    <Skeleton width={labelWidth} height={15} className="mb-2" />
    <div className="d-flex gap-2 align-items-center">
      <Skeleton width="100%" height={fieldHeight} />
      <Skeleton width={30} height={30} />
    </div>
  </div>
);
FormFieldWithIcon.propTypes = {
  labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fieldHeight: PropTypes.number
};

// Reusable table header
const TableHeader = ({ columns }) => (
  <div className="tableHeaderSkeleton">
    <div className="row">
      {columns.map((col, index) => (
        <div key={index} className={col.className}>
          <Skeleton width="100%" height={15} />
        </div>
      ))}
    </div>
  </div>
);
TableHeader.propTypes = {
  columns: PropTypes.array.isRequired
};

// Reusable table row
const TableRow = ({ columns, hasActions = false }) => (
  <div className="row mb-2">
    {columns.map((col, index) => (
      <div key={index} className={col.className}>
        <Skeleton width="100%" height={20} />
      </div>
    ))}
    {hasActions && (
      <div className="col-2 d-flex gap-1">
        <Skeleton width={20} height={20} />
        <Skeleton width={20} height={20} />
      </div>
    )}
  </div>
);
TableRow.propTypes = {
  columns: PropTypes.array.isRequired,
  hasActions: PropTypes.bool
};

const InvoicePageSkeleton = () => {
  // Table configurations
  const feeTableColumns = [
    { className: "col-3" },
    { className: "col-3" },
    { className: "col-3" },
    { className: "col-3" }
  ];

  const paymentTableColumns = [
    { className: "col-2" },
    { className: "col-3" },
    { className: "col-2" },
    { className: "col-3" }
  ];

  const feeTableRows = Array(2).fill(null);
  const paymentTableRows = Array(1).fill(null);

  return (
    <>
      <SectionHeader width="50px" />
      <SkeletonTheme baseColor="#e0e0e0" highlightColor="#e0e0e0">
        <div className="container-fluid p-3">
          <div className="row">
            {/* Left Section */}
            <div className="col-12 col-md-6">
              {/* FUNDTAP INFORMATION */}
              <div className="mb-4">
                <SectionHeader width="200px" />
                <div className="p-3">
                  <div className="row mb-3">
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormFieldWithIcon />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-6">
                      <FormFieldWithIcon />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <SectionHeader width="180px" />
                <div className="p-3">
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormFieldWithIcon />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-3">
                      <FormFieldWithIcon />
                    </div>
                    <div className="col-6">
                      <FormFieldWithIcon />
                    </div>
                    <div className="col-3">
                      <FormFieldWithIcon />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                    <div className="col-3">
                      <FormField />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section */}
            <div className="col-12 col-md-6">
              {/* FEE */}
              <div className="mb-4">
                <SectionHeader width="60px" />
                <div className="p-3">
                  <div className="row mb-3">
                    <div className="col-4">
                      <FormField fieldHeight={25} />
                    </div>
                    <div className="col-4">
                      <FormField fieldHeight={25} />
                    </div>
                    <div className="col-4">
                      <Skeleton width="70%" height={15} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormField fieldHeight={25} />
                    </div>
                    <div className="col-6">
                      <FormField fieldHeight={25} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormField fieldHeight={25} />
                    </div>
                    <div className="col-6">
                      <Skeleton width="60%" height={15} />
                    </div>
                  </div>

                  <TableHeader columns={feeTableColumns} />
                  {feeTableRows.map((_, i) => (
                    <TableRow key={i} columns={feeTableColumns} hasActions />
                  ))}

                  <div className="row mb-3 justify-content-end">
                    <div className="col-2">
                      <Skeleton width={30} height={25} />
                    </div>
                  </div>
                </div>
              </div>

              {/* PAYMENTS */}
              <div className="mb-4">
                <SectionHeader width="120px" />
                <div className="p-3">
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormField />
                    </div>
                    <div className="col-6">
                      <FormFieldWithIcon />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12">
                      <Skeleton width="90%" height={15} />
                    </div>
                  </div>

                  <TableHeader
                    columns={[...paymentTableColumns, { className: "col-2" }]}
                  />
                  {paymentTableRows.map((_, i) => (
                    <TableRow
                      key={i}
                      columns={paymentTableColumns}
                      hasActions
                    />
                  ))}

                  <div className="row mb-3">
                    <div className="col-6">
                      <Skeleton width="80%" height={15} />
                    </div>
                    <div className="col-6">
                      <Skeleton width="60%" height={15} />
                    </div>
                  </div>

                  <div className="row mb-3 justify-content-end">
                    <div className="col-2">
                      <Skeleton width={30} height={25} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SkeletonTheme>
    </>
  );
};

export default InvoicePageSkeleton;
